import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { SUPPLIER_PRODUCT_MAP } from '../types/supplier-product-map.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for supplier↔product equivalence
 * (org/{orgId}/supplier-product-map). Unique business key: (partyId, supplierCode).
 *
 * Firestore-only por ora — foge do padrão `BUSINESS_ALLOWED_DATA_SOURCES` das demais
 * entidades de negócio de propósito.
 *
 * Motivo: esta entidade é escrita também pelo `system-api` (endpoint de ingestão de NF-e do
 * agente VectorHub). O `system-api` é o *servidor* do `/pg/v1` que o `PostgresRestDataSource`
 * chama, então ele não pode registrar essa source sem chamar a si mesmo por HTTP.
 * Ver spec 0030 (§ O nó) e request 0093 (§D2).
 *
 * Destrava quando: o `/pg/v1` for separado num serviço próprio (spec 0030), ou o `system-api`
 * registrar uma source que chame o pg **em processo** (0093 §D2-bis). Aí volta para
 * BUSINESS_ALLOWED_DATA_SOURCES.
 */
export const supplierProductMapDescriptor = {
    name: SUPPLIER_PRODUCT_MAP,
    label: 'Equivalência de produto do parceiro',
    domain: 'fiscal',
    collectionPath: 'org/{orgId}/supplier-product-map',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Maps partner cProd to Product for NF-e reconciliation',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        {
            name: 'partyId',
            label: 'Parceiro',
            type: 'string',
            required: true,
            description: 'FK → Party (emitente da nota — fornecedor ou cliente)',
        },
        {
            name: 'supplierCode',
            label: 'Código no parceiro (cProd)',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.SUPPLIER_PRODUCT_CODE,
            description: 'det/prod/cProd',
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'FK → Product',
        },
        {
            name: 'supplierDescription',
            label: 'Descrição no parceiro',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NAME,
        },
        {
            name: 'supplierBarcode',
            label: 'EAN no parceiro',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.BARCODE,
        },
        {
            name: 'unitMappings',
            label: 'Conversões de unidade',
            type: 'array',
            required: false,
            description: 'Partner uCom → ProductUnit or explicit factor',
        },
        { name: 'lastUsedAt', label: 'Último uso', type: 'date', display: 'datetime', required: false },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'partyId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
        },
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
        },
    ],
};
//# sourceMappingURL=supplier-product-map.descriptor.js.map